import React, {Suspense} from "react";
import startCase from "lodash/startCase";
import {useTranslation} from "@unibuddy/intl";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {LD_CUSTOM_LABEL_STUDENT_AMBASSADOR_FILTER_FIELD} from "shared/constants";
import styled from "styled-components";

import {BlogPostField, MentorField} from "../../../../types/generated";
import {ChatNowButton} from "../../../Blog/components/ChatNowButton/ChatNowButton";
import MentorCardBanner from "../MentorCardBanner/MentorCardBanner";
import MentorCardDegree from "../MentorCardDegree/MentorCardDegree";
import MentorCardName from "../MentorCardName/MentorCardName";
import MentorCardSection from "../MentorCardSection/MentorCardSection";
import MentorCardSeparator from "../MentorCardSeparator/MentorCardSeparator";
import {Flag} from "../../../Buddies/components/Flag/Flag";
import {HasUniversityFeature} from "../../../Feature/HasUniversityFeature";
import OtherSection from "../sections/OtherSection/OtherSection";
import {isFeatureEnabled} from "../../../Utils/FeatureUtils";
import {useUniversity} from "../../../../../src/widgets/modules/UniversityProvider/UniversityProvider";
import {MentorCardSectionItem} from "../MentorCardSectionItem/MentorCardSectionItem";
import {getenv} from "../../../../../src/util/getEnv";

const MentorCardBlogPosts = React.lazy(() => import("../MentorCardBlogPosts/MentorCardBlogPosts"));

const itemSeparator = " · ";

export const yearsValueToTranslationKey: Record<string, string> = {
    Foundation: "foundation",
    "1st Year": "1stYear",
    "2nd Year": "2ndYear",
    "3rd Year": "3rdYear",
    "4th Year": "4thYear",
    "5th Year": "5thYear",
    "6th Year": "6thYear",
    Masters: "masters",
    PhD: "phd",
};

const Container = styled.div`
    max-width: 550px;
    border: 1px solid #e4e8eb;
    border-radius: 6px;
    background-color: white;
`;

const ChatNow = styled.div`
    text-align: center;
    margin-bottom: 15px;
    padding: 0 20px;
`;

const BlogPostsContainer = styled.div`
    padding: 0 15px;
    margin-bottom: 20px;
`;

export interface MentorCardProps {
    universitySlug: string;
    mentor: MentorField;
    blogPosts: BlogPostField[];
    baseUrl: string;
    onSelectPost: (post: BlogPostField) => void;
    showChatNow?: boolean;
    onChatNow?: () => void;
    className?: string;
}

export const MentorCard: React.FC<MentorCardProps> = ({
    universitySlug,
    mentor,
    blogPosts = [],
    baseUrl,
    onSelectPost,
    showChatNow,
    onChatNow,
    className,
}) => {
    const university = useUniversity();
    const DEFAULT_PROFILE_PHOTO = getenv("DEFAULT_PROFILE_PHOTO") as string;

    const {
        profilePhoto = DEFAULT_PROFILE_PHOTO,
        firstName = "",
        degree = {},
        degrees = [],
        degreeLevel = "",
        placeName = "",
        country,
        mentorFields,
        languages = [],
        year = "",
        favouriteModules = [],
        pastQualification = "",
        interests = [],
        societies = [],
        customAttributes = [],
        bio = "",
        lastSeen = null,
    } = mentor || {};

    const countryCode = country?.code || "";
    const primaryFlag = mentorFields?.primaryFlag?.code || "";
    const secondaryFlag = mentorFields?.secondaryFlag?.code || "";

    const hasCustomAttributes = customAttributes.filter((a) => !!a.value).length > 0;
    const {t} = useTranslation(["mentorCard", "languages"]);
    const degreeTypeOrder = university?.degreeTypeOrder || [];
    const isDegreeTypesEnabled = isFeatureEnabled("enableDegreeTypes", university);
    const isShowCountryFlagEnabled = isFeatureEnabled("showCountryFlag", university);

    const degreesByType: Record<string, string[]> = {};
    degrees.forEach((obj) => {
        if (obj.degree) {
            if (degreesByType[obj.degreeType]) degreesByType[obj.degreeType].push(obj.degree.name);
            else degreesByType[obj.degreeType] = [obj.degree.name];
        }
    });

    const orderedDegrees: Array<{degreeType: string; degree: string[]}> = [];
    degreeTypeOrder.forEach((degreeTypeOrderItem) => {
        if (degreesByType[degreeTypeOrderItem.type]) {
            orderedDegrees.push({
                degreeType: degreeTypeOrderItem.type,
                degree: degreesByType[degreeTypeOrderItem.type],
            });
        }
    });

    const isCustomFilterLabelsEnabled = useBooleanFeatureFlag(
        LD_CUSTOM_LABEL_STUDENT_AMBASSADOR_FILTER_FIELD.key,
        LD_CUSTOM_LABEL_STUDENT_AMBASSADOR_FILTER_FIELD.value,
    );
    const customMentorFieldLabels = university?.mentorFieldLabels;

    const getCustomLabel = (key: string, fallback: string): string => {
        if (!isCustomFilterLabelsEnabled) return fallback;
        const label = customMentorFieldLabels?.find((item) => item.key === key)?.customLabel;
        return label || fallback;
    };

    return (
        <Container className={className}>
            <MentorCardBanner
                name={firstName}
                profilePhoto={profilePhoto}
                lastSeen={lastSeen}
                imageClickable={false}
                topLeft={degreeLevel === "Alumni" ? "Alumni" : null}
            />
            <MentorCardName name={firstName} />
            {!isDegreeTypesEnabled ? <MentorCardDegree degree={degree.name} /> : null}
            {showChatNow ? (
                <ChatNow>
                    <ChatNowButton onChatNow={onChatNow || (() => true)} name={firstName} />
                </ChatNow>
            ) : null}
            <MentorCardSeparator />
            <MentorCardSection title={t("summary", "In short")}>
                <MentorCardSectionItem
                    label={getCustomLabel("country", t("iComeFrom", "I come from"))}
                >
                    {placeName}
                    &nbsp;
                    {isShowCountryFlagEnabled && (
                        <Flag
                            countryCode={countryCode}
                            primaryFlag={primaryFlag}
                            secondaryFlag={secondaryFlag}
                        />
                    )}
                </MentorCardSectionItem>

                <MentorCardSectionItem label={t("iSpeak", "I speak")}>
                    {languages.map((l) => t(`languages:${l.name}`, l.name)).join(", ")}
                </MentorCardSectionItem>

                {degreeLevel !== "Alumni" ? (
                    <MentorCardSectionItem
                        label={t("currentlyIn", "I'm currently in")}
                        last
                        data-test-id="yearSection"
                    >
                        {degreeLevel === "Undergraduate"
                            ? t(
                                  `undergraduateYears.${yearsValueToTranslationKey[year] ?? year}`,
                                  year,
                              )
                            : t(
                                  `postgraduateYears.${yearsValueToTranslationKey[year] ?? year}`,
                                  year,
                              )}
                    </MentorCardSectionItem>
                ) : null}
            </MentorCardSection>

            {isDegreeTypesEnabled ? (
                <MentorCardSection
                    title={getCustomLabel("degree", t("areaOfStudySection", "Area(s) of Study"))}
                    data-test-id="areaOfStudySection"
                >
                    {orderedDegrees.map((orderedDegree) => (
                        <MentorCardSectionItem
                            key={orderedDegree.degreeType}
                            label={t(
                                orderedDegree.degreeType.toLowerCase(),
                                `${startCase(orderedDegree.degreeType)}(S)`,
                            )}
                        >
                            {orderedDegree.degree.map((degreeName) => (
                                <div
                                    key={`${orderedDegree.degreeType}${degreeName}`}
                                    data-test-id={`${orderedDegree.degreeType}${degreeName}`}
                                >
                                    {degreeName}
                                </div>
                            ))}
                        </MentorCardSectionItem>
                    ))}
                </MentorCardSection>
            ) : null}

            {degreeLevel !== "Alumni" ? (
                <MentorCardSection
                    title={t("academicLife", "Academic life")}
                    data-test-id="academicSection"
                >
                    <MentorCardSectionItem label={t("favoriteCourses", "Favourite courses")}>
                        {favouriteModules.map((l) => l.name).join(itemSeparator)}
                    </MentorCardSectionItem>

                    <MentorCardSectionItem
                        label={t("previousStudies", "Previous Qualification")}
                        last
                    >
                        {pastQualification}
                    </MentorCardSectionItem>
                </MentorCardSection>
            ) : null}

            {degreeLevel !== "Alumni" ? (
                <MentorCardSection
                    title={t("socialLife", "Social life")}
                    data-test-id="socialSection"
                >
                    <MentorCardSectionItem label={t("hobbiesInterests", "Hobbies & interests")}>
                        {interests.map((l) => l.name).join(itemSeparator)}
                    </MentorCardSectionItem>

                    <MentorCardSectionItem label={t("societies", "Clubs & Societies")} last>
                        {societies.length > 0
                            ? societies.map((l) => l.name).join(itemSeparator)
                            : t("notPartOfSocieties", "I'm not part of any societies")}
                    </MentorCardSectionItem>
                </MentorCardSection>
            ) : null}

            {hasCustomAttributes ? (
                <HasUniversityFeature
                    data-test-id="customAttributesSection"
                    universitySlug={universitySlug}
                    feature="customAmbassadorAttributes"
                >
                    <OtherSection customAttributes={customAttributes} />
                </HasUniversityFeature>
            ) : null}

            <MentorCardSection title={t("aboutMe", "About me")}>
                <MentorCardSectionItem last>{bio}</MentorCardSectionItem>
            </MentorCardSection>

            <Suspense fallback={null}>
                <BlogPostsContainer>
                    <MentorCardBlogPosts
                        blogPosts={blogPosts}
                        onSelectPost={onSelectPost}
                        baseUrl={baseUrl}
                    />
                </BlogPostsContainer>
            </Suspense>
        </Container>
    );
};
